import { FormEvent, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

import { selectDirectory } from "../../api/directoryPicker";
import { serverApi } from "../../api/http";
import { Site } from "../../api/sites";
import { getSshProfiles, SshProfile } from "../../api/sshProfiles";

type PathDraft = {
    text: string;
    homePath: string;
    localPath: string;
    jdkPath: string;
    sshProfileId: string;
    remoteUploadPath: string;
};

type TextField = "text" | "homePath" | "localPath" | "jdkPath" | "remoteUploadPath";
type BrowseField = "localPath" | "jdkPath";

const toDraft = (site: Site): PathDraft => ({
    text: site.text ?? "",
    homePath: site.homePath ?? "",
    localPath: site.localPath ?? "",
    jdkPath: site.jdkPath ?? "",
    sshProfileId: site.sshProfileId == null ? "" : String(site.sshProfileId),
    remoteUploadPath: site.remoteUploadPath ?? "deploykit",
});

const emptyDraft = (): PathDraft => ({
    text: "",
    homePath: "",
    localPath: "",
    jdkPath: "",
    sshProfileId: "",
    remoteUploadPath: "deploykit",
});

export default function PathUpdateModal({
    show,
    onPath,
    initialPathId,
    initialCreateMode = false,
    onOpenSshProfiles,
}: {
    show: boolean;
    onPath: () => void;
    initialPathId?: number | null;
    initialCreateMode?: boolean;
    onOpenSshProfiles?: (profileId?: number, siteId?: number) => void;
}) {
    const [pathList, setPathList] = useState<Site[]>([]);
    const [profiles, setProfiles] = useState<SshProfile[]>([]);
    const [selectedPathId, setSelectedPathId] = useState<number | null>(null);
    const [draft, setDraft] = useState<PathDraft | null>(null);
    const [searchText, setSearchText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!show) return;
        let cancelled = false;
        setIsLoading(true);
        setSearchText("");
        Promise.all([
            serverApi.get<Site[]>("/api/pathList").then((response) => response.data),
            getSshProfiles(),
        ])
            .then(([paths, sshProfiles]) => {
                if (cancelled) return;
                const initial = paths.find((path) => path.id === initialPathId) ?? paths[0] ?? null;
                setPathList(paths);
                setProfiles(sshProfiles);
                setSelectedPathId(initialCreateMode ? null : initial?.id ?? null);
                setDraft(initialCreateMode || !initial ? emptyDraft() : toDraft(initial));
            })
            .catch(async (error) => {
                console.error("path manager load failed", error);
                if (!cancelled) {
                    await Swal.fire({ icon: "error", title: "경로 조회 실패", text: "저장된 경로를 불러오지 못했습니다." });
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [initialCreateMode, initialPathId, show]);

    const selectedPath = useMemo(
        () => pathList.find((path) => path.id === selectedPathId) ?? null,
        [pathList, selectedPathId]
    );
    const selectedProfile = useMemo(
        () => profiles.find((profile) => String(profile.id) === draft?.sshProfileId) ?? null,
        [draft?.sshProfileId, profiles]
    );
    const filteredPaths = useMemo(() => {
        const query = searchText.trim().toLocaleLowerCase();
        if (!query) return pathList;
        return pathList.filter((path) =>
            [path.text, path.homePath, path.localPath, path.sshProfileName]
                .some((value) => value?.toLocaleLowerCase().includes(query))
        );
    }, [pathList, searchText]);
    const isDirty = Boolean(
        draft &&
        JSON.stringify(selectedPath ? toDraft(selectedPath) : emptyDraft()) !== JSON.stringify(draft)
    );
    const sshProfileChanged = Boolean(
        draft && selectedPath && toDraft(selectedPath).sshProfileId !== draft.sshProfileId
    );

    if (!show) return null;

    const confirmDiscard = async () => {
        if (!isDirty) return true;
        const result = await Swal.fire({
            icon: "warning",
            title: "저장하지 않은 변경 사항",
            text: "변경 내용을 저장하지 않고 이동할까요?",
            showCancelButton: true,
            confirmButtonText: "이동",
            cancelButtonText: "취소",
            reverseButtons: true,
        });
        return result.isConfirmed;
    };

    const selectPath = async (path: Site) => {
        if (path.id === selectedPathId || !(await confirmDiscard())) return;
        setSelectedPathId(path.id);
        setDraft(toDraft(path));
    };

    const createPath = async () => {
        if (!(await confirmDiscard())) return;
        setSelectedPathId(null);
        setDraft(emptyDraft());
        setSearchText("");
    };

    const requestClose = async () => {
        if (await confirmDiscard()) onPath();
    };

    const updateDraft = (field: keyof PathDraft, value: string) => {
        setDraft((current) => (current ? { ...current, [field]: value } : current));
    };

    const browse = async (field: BrowseField, title: string) => {
        if (!draft) return;
        try {
            const selectedDirectory = await selectDirectory(draft[field], title);
            if (selectedDirectory) updateDraft(field, selectedDirectory);
        } catch (error) {
            console.error("directory picker failed", error);
            await Swal.fire({ icon: "error", title: "경로 선택 실패", text: "폴더 선택 창을 열지 못했습니다." });
        }
    };

    const save = async (event: FormEvent) => {
        event.preventDefault();
        if (!draft || isSaving) return;
        const normalized: PathDraft = {
            ...draft,
            text: draft.text.trim(),
            homePath: draft.homePath.trim(),
            localPath: draft.localPath.trim(),
            jdkPath: draft.jdkPath.trim(),
            remoteUploadPath: draft.remoteUploadPath.trim(),
        };
        if (!normalized.text || !normalized.homePath || !normalized.localPath) {
            await Swal.fire({
                icon: "warning",
                title: "입력 확인",
                text: "사이트 이름, 배포 서버 경로, 로컬 프로젝트 경로를 입력해 주세요.",
            });
            return;
        }

        setIsSaving(true);
        try {
            if (!selectedPath) {
                const response = await serverApi.post<Site>("/api/savedPath", {
                    text: normalized.text,
                    homePath: normalized.homePath,
                    localPath: normalized.localPath,
                    jdkPath: normalized.jdkPath,
                    ...(normalized.sshProfileId
                        ? {
                              sshProfileId: Number(normalized.sshProfileId),
                              remoteUploadPath: normalized.remoteUploadPath || "deploykit",
                          }
                        : {}),
                });
                const created = response.data;
                setPathList((current) => [
                    ...current.filter((path) => path.id !== created.id),
                    created,
                ]);
                setSelectedPathId(created.id);
                setDraft(toDraft(created));
                await Swal.fire({ icon: "success", title: "경로를 등록했습니다.", timer: 1000, showConfirmButton: false });
                return;
            }

            const original = toDraft(selectedPath);
            const changedFields = (Object.keys(normalized) as Array<keyof PathDraft>)
                .filter((field) => normalized[field] !== original[field]);
            if (changedFields.length === 0) return;

            await Promise.all(changedFields.map((field) =>
                serverApi.post("/api/updatePath", {
                    id: selectedPath.id,
                    field,
                    value: normalized[field],
                })
            ));
            const profile = profiles.find((item) => String(item.id) === normalized.sshProfileId);
            const updated: Site = {
                ...selectedPath,
                text: normalized.text,
                homePath: normalized.homePath,
                localPath: normalized.localPath,
                jdkPath: normalized.jdkPath,
                sshProfileId: profile?.id,
                sshProfileName: profile?.name,
                sshHost: profile?.sshHost,
                sshPort: profile?.sshPort,
                sshUsername: profile?.sshUsername,
                sshAuthType: profile?.sshAuthType,
                sshPrivateKeyPath: profile?.sshPrivateKeyPath,
                strictHostKeyChecking: profile?.strictHostKeyChecking,
                remoteUploadPath: normalized.remoteUploadPath,
            };
            setPathList((current) => current.map((path) => path.id === updated.id ? updated : path));
            setDraft(normalized);
            await Swal.fire({ icon: "success", title: "경로를 저장했습니다.", timer: 1000, showConfirmButton: false });
        } catch (error) {
            console.error("path save failed", error);
            await Swal.fire({
                icon: "error",
                title: selectedPath ? "경로 저장 실패" : "경로 등록 실패",
                text: "경로 정보를 저장하지 못했습니다.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const remove = async () => {
        if (!selectedPath || isSaving) return;
        const result = await Swal.fire({
            icon: "warning",
            title: "배포 경로 삭제",
            text: `${selectedPath.text} 경로를 삭제할까요? SSH 프로필은 삭제되지 않습니다.`,
            showCancelButton: true,
            confirmButtonText: "삭제",
            cancelButtonText: "취소",
            reverseButtons: true,
        });
        if (!result.isConfirmed) return;
        setIsSaving(true);
        try {
            await serverApi.post("/api/deletePath", { id: selectedPath.id, useYn: "N" });
            const remaining = pathList.filter((path) => path.id !== selectedPath.id);
            const next = remaining[0] ?? null;
            setPathList(remaining);
            setSelectedPathId(next?.id ?? null);
            setDraft(next ? toDraft(next) : null);
        } finally {
            setIsSaving(false);
        }
    };

    const renderInput = (field: TextField, label: string, placeholder: string, browseField?: BrowseField) => (
        <label className={`dp-form-field${field === "localPath" || field === "jdkPath" ? " is-wide" : ""}`}>
            <span>{label}</span>
            {browseField ? (
                <div className="dp-form-browse">
                    <input className="form-control" value={draft?.[field] ?? ""} readOnly placeholder={placeholder} />
                    <button type="button" onClick={() => void browse(browseField, label)}>폴더 선택</button>
                </div>
            ) : (
                <input className="form-control" value={draft?.[field] ?? ""} placeholder={placeholder} onChange={(event) => updateDraft(field, event.currentTarget.value)} />
            )}
        </label>
    );

    return (
        <div className="modal dp-modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered dp-modal-dialog dp-path-manager-dialog">
                <div className="modal-content dp-modal-content dp-path-manager">
                    <div className="modal-header dp-modal-header dp-path-manager-header">
                        <div><h5 className="modal-title">경로 관리</h5><span>{pathList.length}개 경로</span></div>
                        <button type="button" className="btn-close" aria-label="닫기" onClick={() => void requestClose()} />
                    </div>
                    <div className="dp-path-manager-body">
                        <aside className="dp-path-manager-sidebar">
                            <div className="dp-path-manager-sidebar-title">
                                <strong>배포 경로</strong>
                                <button type="button" className="dp-profile-add" onClick={() => void createPath()}>+ 새 경로</button>
                            </div>
                            <input type="search" className="dp-path-manager-search" aria-label="경로 검색" placeholder="경로 검색" value={searchText} onChange={(event) => setSearchText(event.currentTarget.value)} />
                            <div className="dp-path-manager-list" role="listbox">
                                {filteredPaths.map((path) => (
                                    <button key={path.id} type="button" role="option" aria-selected={path.id === selectedPathId} className={`dp-path-manager-item${path.id === selectedPathId ? " is-active" : ""}`} onClick={() => void selectPath(path)}>
                                        <span className={`dp-path-manager-status${path.sshProfileId ? " is-connected" : ""}`} />
                                        <span className="dp-path-manager-item-copy"><strong>{path.text}</strong><small>{path.homePath}</small></span>
                                    </button>
                                ))}
                                {!isLoading && filteredPaths.length === 0 && <div className="dp-path-manager-empty">등록된 경로가 없습니다.</div>}
                            </div>
                        </aside>
                        <section className="dp-path-manager-detail">
                            {draft ? (
                                <form className="dp-path-manager-form" onSubmit={save}>
                                    <div className="dp-path-manager-detail-header">
                                        <div><span>{selectedPath ? "선택한 경로" : "새 경로 등록"}</span><h6>{selectedPath?.text || "배포 경로 추가"}</h6></div>
                                        {selectedPath && (
                                            <button type="button" className={`dp-path-manager-ssh${selectedProfile ? " is-connected" : ""}`} onClick={() => onOpenSshProfiles?.(selectedProfile?.id, selectedPath.id)}>
                                                <span />
                                                <div><strong>{selectedProfile ? selectedProfile.name : "SSH 연결 안 됨"}</strong><small>{selectedProfile ? `${selectedProfile.sshUsername}@${selectedProfile.sshHost}:${selectedProfile.sshPort}` : "SSH 프로필을 선택하거나 등록하세요."}</small></div>
                                            </button>
                                        )}
                                    </div>
                                    <div className="dp-path-manager-fields dp-path-manager-fields-simple">
                                        {renderInput("text", "사이트 이름", "사이트 이름")}
                                        {renderInput("homePath", "배포 서버 경로", "/usr/local/tomcat/webapps/ROOT")}
                                        {renderInput("localPath", "로컬 프로젝트 경로", "프로젝트 폴더 선택", "localPath")}
                                        {renderInput("jdkPath", "JDK 경로", "JDK 폴더 선택", "jdkPath")}
                                        <div className="dp-path-profile-section is-wide">
                                            <div className="dp-section-heading"><div><strong>SSH 연결</strong><small>이 경로에 연결할 프로필을 선택하세요.</small></div></div>
                                            <div className="dp-path-profile-grid">
                                                <label className={`dp-form-field dp-path-profile-field${sshProfileChanged ? " is-changed" : ""}`}>
                                                    <span>SSH 프로필</span>
                                                    <select aria-label="SSH 프로필" className="form-select" value={draft.sshProfileId} onChange={(event) => updateDraft("sshProfileId", event.currentTarget.value)}>
                                                        <option value="">연결 안 함</option>
                                                        {profiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.name} · {profile.sshUsername}@{profile.sshHost}:{profile.sshPort}</option>)}
                                                    </select>
                                                </label>
                                                <label className="dp-form-field"><span>원격 업로드 경로</span><input className="form-control" value={draft.remoteUploadPath} disabled={!draft.sshProfileId} onChange={(event) => updateDraft("remoteUploadPath", event.currentTarget.value)} /></label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="dp-path-manager-actions">
                                        {selectedPath ? <button type="button" className="dp-path-manager-delete" onClick={() => void remove()} disabled={isSaving}>삭제</button> : <span />}
                                        <div className="dp-path-manager-action-group">
                                            <button type="button" className="dp-path-manager-close" onClick={() => void requestClose()}>닫기</button>
                                            <button type="submit" className="dp-path-manager-save" disabled={!isDirty || isSaving}>{isSaving ? "저장 중" : selectedPath ? "변경 저장" : "경로 등록"}</button>
                                        </div>
                                    </div>
                                </form>
                            ) : (
                                <div className="dp-path-manager-detail-empty"><strong>선택한 경로가 없습니다.</strong></div>
                            )}
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}
